import { useEffect, useState } from "react";
import { collection, doc, getDoc, getDocs, deleteDoc, updateDoc, query, orderBy } from "firebase/firestore";
import { db } from "../../firebase";
import { CLUB_ID, THEME_COLOR } from "../../config/club";
import Swal from "sweetalert2";
export default function ReservationDetailsModal({ show, onClose, reservationId }) {
    const [reservation, setReservation] = useState(null);
    const [players, setPlayers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [logs, setLogs] = useState([]);

    const themeAlpha = (opacity) => `${THEME_COLOR}${opacity}`;

    const loadData = async () => {
        if (!reservationId) return;
        setLoading(true);
        try {
            const ref = doc(db, "clubs", CLUB_ID, "reservations", reservationId);
            const snap = await getDoc(ref);
            if (!snap.exists()) return;

            setReservation({ id: snap.id, ...snap.data() });

            const playersSnap = await getDocs(
                collection(db, "clubs", CLUB_ID, "reservations", reservationId, "players")
            );

            const sortedPlayers = playersSnap.docs
                .map((d) => ({ id: d.id, ...d.data() }))
                .sort((a, b) => (a.joinedAt?.toMillis?.() || 0) - (b.joinedAt?.toMillis?.() || 0));

            setPlayers(sortedPlayers);
            const logsSnap = await getDocs(
                query(
                    collection(
                        db,
                        "clubs",
                        CLUB_ID,
                        "reservations",
                        reservationId,
                        "logs"
                    ),
                    orderBy("createdAt", "desc")
                )
            );

            setLogs(
                logsSnap.docs.map((d) => ({
                    id: d.id,
                    ...d.data(),
                }))
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (show) loadData();
    }, [show, reservationId]);

    if (!show) return null;

    const reserved = players.filter((p) => p.status === "reserved");
    const waitlist = players.filter((p) => p.status === "waitlisted");

    const removePlayer = async (playerId) => {
        const confirm = await Swal.fire({
            title: "Remove player?",
            text: "This will free up their slot immediately.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: THEME_COLOR,
            cancelButtonColor: "#d33",
        });

        if (!confirm.isConfirmed) return;

        await deleteDoc(doc(db, "clubs", CLUB_ID, "reservations", reservationId, "players", playerId));
        await updateDoc(doc(db, "clubs", CLUB_ID, "reservations", reservationId), {
            reservedCount: Math.max(reserved.length - 1, 0),
        });
        loadData();
    };

    const promotePlayer = async (player) => {
        if (reserved.length >= reservation.maxSlots) {
            Swal.fire("Full", "No more slots available in the main list.", "info");
            return;
        }

        await updateDoc(doc(db, "clubs", CLUB_ID, "reservations", reservationId, "players", player.id), {
            status: "reserved",
        });
        await updateDoc(doc(db, "clubs", CLUB_ID, "reservations", reservationId), {
            reservedCount: reserved.length + 1,
        });
        loadData();
    };

    const Avatar = ({ player }) => {
        const joinedAt = player.joinedAt?.toDate?.();

        return (
            <div className="d-flex align-items-center gap-3">
                <img
                    src={player.photoURL || `https://ui-avatars.com/api/?name=${player.displayName || "P"}&background=random`}
                    className="rounded-circle border border-2 border-white shadow-sm"
                    style={{ width: 42, height: 42, objectFit: "cover" }}
                    alt="avatar"
                />
                <div className="d-flex flex-column">
                    <span className="fw-bold mb-0" style={{ fontSize: "0.9rem" }}>
                        {player.displayName || "Anonymous"}
                    </span>
                    <span className="text-muted" style={{ fontSize: "0.75rem" }}>
                        {player.realName || "No Real Name"}
                    </span>

                    {joinedAt && (
                        <span className="text-muted" style={{ fontSize: "0.7rem" }}>
                            Joined: {joinedAt.toLocaleString(undefined, {
                                month: "short",
                                day: "numeric",
                                hour: "numeric",
                                minute: "2-digit",
                            })}
                        </span>
                    )}
                </div>
            </div>
        );
    };

    const generateReservationText = () => {
        if (!reservation) return "";

        const dateObj = new Date(reservation.date);
        const formattedDate = dateObj.toLocaleDateString(undefined, {
            month: "long",
            day: "numeric",
            year: "numeric",
            weekday: "short",
        });

        const formatTime = (time) => {
            const d = new Date(`1970-01-01T${time}`);
            return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
        };

        const start = formatTime(reservation.startTime);
        const end = formatTime(reservation.endTime);

        return `${reservation.name?.toUpperCase() || "EVENT"}

COURTS: 3-4
DATE: ${formattedDate}
TIME: ${start} - ${end}

FEE: 280
GCash: Manuel Christian M. — 09970985227

Have fun and be on time!

${players
                .filter(p => p.status === "reserved")
                .map((p, i) => `${i + 1}. ${p.displayName || "Anonymous"}`)
                .join("\n")}
`;
    };

    const copyToClipboard = async () => {
        const text = generateReservationText();
        await navigator.clipboard.writeText(text);

        Swal.fire({
            icon: "success",
            title: "Copied!",
            text: "Reservation details copied to clipboard.",
            timer: 1500,
            showConfirmButton: false,
        });
    };

    return (
        <div
            className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center animate__animated animate__fadeIn"
            style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(10px)", zIndex: 1060 }}
            onClick={onClose}
        >
            <div
                className="bento-card border-0 shadow-lg animate__animated animate__slideInUp"
                style={{ width: "100%", maxWidth: "550px", maxHeight: "85vh", overflow: "hidden", display: "flex", flexDirection: "column", backgroundColor: 'var(--card-bg)' }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* HEADER */}
                <div className="p-4 border-bottom border-light d-flex justify-content-between align-items-start bg-surface">
                    <div>
                        <span className="rank-label text-primary mb-1 d-block" style={{ color: THEME_COLOR }}>Management Console</span>
                        <h4 className="fw-black mb-1">{reservation?.name}</h4>
                        <p className="text-muted small mb-0">
                            <i className="bi bi-clock me-1"></i>
                            {reservation?.date && reservation?.startTime && reservation?.endTime && (() => {
                                const start = new Date(`${reservation.date}T${reservation.startTime}`);
                                const end = new Date(`${reservation.date}T${reservation.endTime}`);

                                return `${start.toLocaleDateString(undefined, {
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric",
                                })} • ${start.toLocaleTimeString([], {
                                    hour: "numeric",
                                    minute: "2-digit",
                                })} - ${end.toLocaleTimeString([], {
                                    hour: "numeric",
                                    minute: "2-digit",
                                })}`;
                            })()}
                        </p>
                    </div>
                    <button className="theme-toggle-btn border-0" onClick={onClose} style={{ width: 32, height: 32 }}>
                        <i className="bi bi-x-lg"></i>
                    </button>
                </div>

                {/* PROGRESS MINI BAR */}
                <div className="px-4 py-3 d-flex align-items-center justify-content-between border-bottom border-light">
                    <span className="small fw-bold">Current Occupancy</span>
                    <span className="badge rounded-pill fw-black bg-light" style={{ background: themeAlpha("15"), color: THEME_COLOR }}>
                        {reserved.length} / {reservation?.maxSlots} Players
                    </span>
                </div>

                <div className="p-4 overflow-auto custom-scrollbar" style={{ flexGrow: 1 }}>
                    {/* RESERVED LIST */}
                    <section className="mb-5">
                        <button
                            className="btn btn-sm btn-primary rounded-pill px-3 fw-bold"
                            onClick={copyToClipboard}
                        >
                            Copy Format
                        </button>
                        <div className="d-flex align-items-center gap-2 mb-3">
                            <div className="p-1 bg-success rounded-circle" style={{ width: 8, height: 8 }}></div>
                            <h6 className="rank-label m-0 text-success">Reserved List</h6>
                        </div>

                        <div className="d-flex flex-column gap-2">
                            {reserved.length === 0 && <p className="text-muted small italic p-3 text-center border rounded-3 border-dashed">No players confirmed yet.</p>}
                            {reserved.map((p) => (
                                <div key={p.id} className="p-3 rounded-4 d-flex justify-content-between align-items-center transition-all bg-card border border-light">
                                    <Avatar player={p} />
                                    <button className="btn btn-sm btn-light border rounded-pill px-3 text-danger fw-bold" onClick={() => removePlayer(p.id)}>
                                        Kick
                                    </button>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* WAITLIST */}
                    <section>
                        <div className="d-flex align-items-center gap-2 mb-3">
                            <div className="p-1 bg-warning rounded-circle" style={{ width: 8, height: 8 }}></div>
                            <h6 className="rank-label m-0 text-warning">Waitlisted ({waitlist.length})</h6>
                        </div>

                        <div className="d-flex flex-column gap-2">
                            {waitlist.map((p) => (
                                <div key={p.id} className="p-3 rounded-4 d-flex justify-content-between align-items-center bg-card border border-light opacity-75">
                                    <Avatar player={p} />
                                    <div className="d-flex gap-2">
                                        <button className="btn btn-sm btn-success rounded-pill px-3 shadow-sm fw-bold" onClick={() => promotePlayer(p)}>
                                            Promote
                                        </button>
                                        <button className="theme-toggle-btn border-0 text-danger" style={{ width: 32, height: 32 }} onClick={() => removePlayer(p.id)}>
                                            <i className="bi bi-trash"></i>
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                    {/* LOGS */}
                    <section className="mt-5">
                        <div className="d-flex align-items-center gap-2 mb-3">
                            <div
                                className="p-1 bg-danger rounded-circle"
                                style={{ width: 8, height: 8 }}
                            ></div>

                            <h6 className="rank-label m-0 text-danger">
                                Activity Logs
                            </h6>
                        </div>

                        <div className="d-flex flex-column gap-2">
                            {logs.length === 0 && (
                                <p className="text-muted small italic p-3 text-center border rounded-3 border-dashed">
                                    No logs yet.
                                </p>
                            )}

                            {logs.map((log) => (
                                <div
                                    key={log.id}
                                    className="p-3 rounded-4 bg-card border border-light"
                                >
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div>
                                            <div className="fw-bold small">
                                                {log.playerName}
                                            </div>

                                            <div className="text-muted small">
                                                left the reservation
                                            </div>

                                            {log.statusBeforeLeave && (
                                                <div
                                                    className="small mt-1"
                                                    style={{ color: THEME_COLOR }}
                                                >
                                                    Previous status:{" "}
                                                    {log.statusBeforeLeave.toUpperCase()}
                                                </div>
                                            )}
                                        </div>

                                        <div className="text-muted small text-end">
                                            {
                                                `${log.createdAt?.toDate?.()?.toLocaleDateString(undefined, {
                                                    month: "short",
                                                    day: "numeric",
                                                    year: "numeric",
                                                })} • ${log.createdAt
                                                    ?.toDate?.()
                                                    ?.toLocaleTimeString([], {
                                                        hour: "numeric",
                                                        minute: "2-digit",
                                                    })}`
                                            }
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                </div>

                {/* FOOTER */}
                <div className="p-3 border-top border-light text-center bg-surface">
                    <button className="btn btn-link text-muted small text-decoration-none" onClick={onClose}>Close Overview</button>
                </div>
            </div>
        </div>
    );
}